import {
    AggregationSurveyIntroduction,
    AggregationSurveyIntroductionMetric,
    SurveyIntroductionMetric,
    SurveyIntroductionUnit,
} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {comparisonOf} from "../../../insights/kit/Headline";
import {StatCardData, StatTarget} from "../../../insights/kit/StatCard";
import {formatSurveyFillOutTime, formatSurveyTimeToRespond} from "../base/surveyDurations";

type Translate = (key: string, options?: Record<string, unknown>) => string;

export type SurveyTargetMetricId =
    | 'crqlarScore'
    | 'satisfaction'
    | 'nps'
    | 'invitations'
    | 'submissions'
    | 'responseRate'
    | 'timeToRespondHours'
    | 'fillOutTimeMinutes';

interface SurveyTargetMetric {
    id: SurveyTargetMetricId;
    metric: SurveyIntroductionMetric;
    labelKey: string;
    min: number;
    max: number;
    unitKey?: string;
}

export type SurveyChartTargetId =
    | 'responsesPerDay'
    | 'completionRate';

interface SurveyChartTarget {
    id: SurveyChartTargetId;
    labelKey: string;
    unitKey?: string;
    min: number;
    max: number;
}

export const SURVEY_CHART_TARGETS: SurveyChartTarget[] = [
    {id: 'responsesPerDay', labelKey: 'Responses per day', min: 1, max: 10000},
    {id: 'completionRate', labelKey: 'Completion', unitKey: '% of started', min: 1, max: 100},
];

export const SURVEY_TARGET_METRICS: SurveyTargetMetric[] = [
    {id: 'crqlarScore', metric: SurveyIntroductionMetric.CRQLAR_SCORE, labelKey: 'Crqlar score', min: 0, max: 100},
    {id: 'satisfaction', metric: SurveyIntroductionMetric.SATISFACTION, labelKey: 'Satisfaction', min: 0, max: 100},
    {id: 'nps', metric: SurveyIntroductionMetric.NPS, labelKey: 'NPS', min: -100, max: 100},
    {id: 'invitations', metric: SurveyIntroductionMetric.INVITATIONS, labelKey: 'Invitations', min: 0, max: 100000},
    {id: 'submissions', metric: SurveyIntroductionMetric.SUBMISSIONS, labelKey: 'Submissions', min: 0, max: 100000},
    {id: 'responseRate', metric: SurveyIntroductionMetric.RESPONSE_RATE, labelKey: 'Response rate', min: 0, max: 100},
    {
        id: 'timeToRespondHours',
        metric: SurveyIntroductionMetric.TIME_TO_RESPOND,
        labelKey: 'Time to respond',
        min: 1,
        max: 720,
        unitKey: 'hours',
    },
    {
        id: 'fillOutTimeMinutes',
        metric: SurveyIntroductionMetric.FILL_OUT_TIME,
        labelKey: 'Fill-out time',
        min: 1,
        max: 1440,
        unitKey: 'minutes',
    },
];

const HINT_KEYS: Record<SurveyIntroductionMetric, string> = {
    [SurveyIntroductionMetric.CRQLAR_SCORE]: 'Answer quality on a scale from 0 to 100.',
    [SurveyIntroductionMetric.SATISFACTION]: 'Share of rating answers in the top box - 8 or more of 10, or 4 stars and up.',
    [SurveyIntroductionMetric.NPS]: 'Promoters minus detractors on the recommendation question, from -100 to 100.',
    [SurveyIntroductionMetric.INVITATIONS]: 'Invitations sent in the period, voided ones excluded.',
    [SurveyIntroductionMetric.SUBMISSIONS]: 'Surveys submitted in the period, matching the filters.',
    [SurveyIntroductionMetric.RESPONSE_RATE]: 'Submissions as a share of the invitations sent in the period.',
    [SurveyIntroductionMetric.TIME_TO_RESPOND]: 'Median time from invitation to submission.',
    [SurveyIntroductionMetric.FILL_OUT_TIME]: 'Median time from first answer to submission.',
};

const round = (value: number) => Math.round(value);

export const formatIntroductionValue = (metric: SurveyIntroductionMetric, unit: SurveyIntroductionUnit, value: number, t: Translate): string => {
    switch (unit) {
        case SurveyIntroductionUnit.PERCENT:
            return `${round(value)}%`;
        case SurveyIntroductionUnit.SECONDS:
            return metric === SurveyIntroductionMetric.FILL_OUT_TIME
                ? formatSurveyFillOutTime(value, t)
                : formatSurveyTimeToRespond(value, t);
        case SurveyIntroductionUnit.POINTS:
            return metric === SurveyIntroductionMetric.NPS && value > 0 ? `+${round(value)}` : String(round(value));
        case SurveyIntroductionUnit.COUNT:
        default:
            return String(round(value));
    }
};

export const formatIntroductionChange = (metric: SurveyIntroductionMetric, unit: SurveyIntroductionUnit, magnitude: number, t: Translate): string =>
    metric === SurveyIntroductionMetric.NPS ? String(round(magnitude)) : formatIntroductionValue(metric, unit, magnitude, t);

const targetOf = (figure: AggregationSurveyIntroductionMetric, t: Translate): StatTarget | undefined => {
    if (figure.target == null) return undefined;

    return {
        label: t('Target {{value}}', {value: formatIntroductionValue(figure.metric, figure.unit, figure.target.value, t)}),
        progress: figure.target.progress,
        achieved: figure.target.achieved,
    };
};

/** The band's cards: every metric, or only `metrics`, still in the band's order. */
export const toStatCards = (
    introduction: AggregationSurveyIntroduction | undefined,
    t: Translate,
    metrics?: SurveyIntroductionMetric[]
): StatCardData[] =>
    SURVEY_TARGET_METRICS.filter(definition => metrics == null || metrics.includes(definition.metric)).map(definition => {
        const figure = introduction?.metrics.find(entry => entry.metric === definition.metric);

        return {
            id: definition.labelKey,
            title: t(definition.labelKey),
            hint: t(HINT_KEYS[definition.metric]),
            value: figure?.value == null ? '—' : formatIntroductionValue(figure.metric, figure.unit, figure.value, t),
            comparison: figure == null ? undefined : comparisonOf(
                figure.value,
                figure.previous,
                value => formatIntroductionValue(figure.metric, figure.unit, value, t),
                figure.lowerIsBetter,
                magnitude => formatIntroductionChange(figure.metric, figure.unit, magnitude, t)
            ),
            target: figure ? targetOf(figure, t) : undefined,
        };
    });

